import { TimeRangeItem } from './Constants'
import { TimeRange } from '../common/component/DateRangeSelector'

export type TimeRangeLike = {
  timeRangeItem: TimeRangeItem
  from: number | Date
  to: number | Date
}

const toDate = (value: number | Date): Date =>
  value instanceof Date ? value : new Date(value)

const createUtcDate = (year: number, monthIndex: number, day: number): Date =>
  new Date(Date.UTC(year, monthIndex, day))

const getDayBounds = (date: Date): {start: Date; end: Date} => ({
  start: new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0)
  ),
  end: new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999)
  ),
})

export const recalibrateTimeRange = (timeRange: TimeRangeLike): TimeRange => {
  const {timeRangeItem} = timeRange

  if (timeRangeItem.metric === 'CUSTOM') {
    const from = toDate(timeRange.from)
    const to = toDate(timeRange.to)
    return {
      timeRangeItem,
      from: getDayBounds(from).start,
      to: getDayBounds(to).end,
    }
  }

  const now = new Date()
  const nowYear = now.getUTCFullYear()
  const nowMonth = now.getUTCMonth()
  const nowDay = now.getUTCDate()
  let fromDateSelected: Date
  let toDateSelected: Date

  switch (timeRangeItem.metric) {
    case 'H': {
      const fromByHour = new Date(now.getTime() - timeRangeItem.value * 60 * 60 * 1000)
      fromDateSelected = createUtcDate(
        fromByHour.getUTCFullYear(),
        fromByHour.getUTCMonth(),
        fromByHour.getUTCDate()
      )
      toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
      break
    }
    case 'D': {
      const fromByDay = new Date(now.getTime() - timeRangeItem.value * 24 * 60 * 60 * 1000)
      fromDateSelected = createUtcDate(
        fromByDay.getUTCFullYear(),
        fromByDay.getUTCMonth(),
        fromByDay.getUTCDate()
      )
      toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
      break
    }
    case 'M':
      fromDateSelected = createUtcDate(nowYear, nowMonth - timeRangeItem.value, nowDay)
      toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
      break
    case 'Y':
      fromDateSelected = createUtcDate(nowYear - timeRangeItem.value, nowMonth, nowDay)
      toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
      break
    case 'TODAY':
      fromDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
      toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
      break
    case 'YESTERDAY':
      fromDateSelected = createUtcDate(nowYear, nowMonth, nowDay - 1)
      toDateSelected = createUtcDate(nowYear, nowMonth, nowDay - 1)
      break
    case 'WEEK': {
      const currentDay = now.getUTCDay()
      const daysSinceMonday = currentDay === 0 ? 6 : currentDay - 1
      fromDateSelected = createUtcDate(nowYear, nowMonth, nowDay - daysSinceMonday)
      toDateSelected = createUtcDate(
        fromDateSelected.getUTCFullYear(),
        fromDateSelected.getUTCMonth(),
        fromDateSelected.getUTCDate() + 6
      )
      break
    }
    case 'LAST_WEEK': {
      const lastWeekCurrentDay = now.getUTCDay()
      const lastWeekDaysSinceMonday = lastWeekCurrentDay === 0 ? 6 : lastWeekCurrentDay - 1
      const currentWeekMonday = createUtcDate(
        nowYear,
        nowMonth,
        nowDay - lastWeekDaysSinceMonday
      )
      fromDateSelected = createUtcDate(
        currentWeekMonday.getUTCFullYear(),
        currentWeekMonday.getUTCMonth(),
        currentWeekMonday.getUTCDate() - 7
      )
      toDateSelected = createUtcDate(
        fromDateSelected.getUTCFullYear(),
        fromDateSelected.getUTCMonth(),
        fromDateSelected.getUTCDate() + 6
      )
      break
    }
    case 'MONTH':
      fromDateSelected = createUtcDate(nowYear, nowMonth, 1)
      toDateSelected = createUtcDate(nowYear, nowMonth + 1, 0)
      break
    default:
      fromDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
      toDateSelected = createUtcDate(nowYear, nowMonth, nowDay)
      break
  }

  return {
    timeRangeItem,
    from: getDayBounds(fromDateSelected).start,
    to: getDayBounds(toDateSelected).end,
  }
}
